/**
 * Centralized "unfinalised creation draft" predicate + write helpers (spec 041).
 *
 * A draft project (mid-creation, not yet confirmed) is a normal `status='created'`
 * row carrying `metadata.creation.draft === true` — NOT a distinct `project_status`
 * enum value (see spec 041 research D1 / plan R1: the enum re-add broke the
 * outcome_type trigger + the DiscoveryOS ontology). Because a draft is otherwise a
 * `created` project, EVERY member-facing project reader must exclude it, and that
 * exclusion is centralized here (R5) so no endpoint forgets it.
 *
 * This module is PURE (no drizzle) so both client and server can import the
 * predicate; the server-side SQL WHERE fragment lives in `project-draft-sql.ts`.
 */

/** JSONB path (Postgres `#>>`) to the draft marker: `metadata -> creation -> draft`. */
export const DRAFT_META_PATH = ['creation', 'draft'] as const;

export interface DraftableProjectRow {
  status?: string | null;
  metadata?: unknown;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** True iff this project row is an unfinalised creation draft (hidden until finalized). */
export function isDraftProject(row: DraftableProjectRow | null | undefined): boolean {
  const meta = asRecord(row?.metadata);
  const creation = asRecord(meta?.creation);
  return creation?.draft === true;
}

/** Drop flagged drafts from a list (client-side belt-and-braces; new array, no mutation). */
export function excludeDrafts<T extends DraftableProjectRow>(rows: T[]): T[] {
  return rows.filter((row) => !isDraftProject(row));
}

/** Set `metadata.creation.draft = true` (immutable merge; tolerates null/non-object). */
export function withDraftFlag(metadata: unknown): Record<string, unknown> {
  const base = asRecord(metadata) ?? {};
  const creation = asRecord(base.creation) ?? {};
  return { ...base, creation: { ...creation, draft: true } };
}

/** Remove the draft flag on finalize (immutable; drops `creation` if it becomes empty). */
export function clearDraftFlag(metadata: unknown): Record<string, unknown> {
  const base = { ...(asRecord(metadata) ?? {}) };
  const creation = asRecord(base.creation);
  if (creation) {
    const { draft: _draft, ...rest } = creation;
    if (Object.keys(rest).length > 0) base.creation = rest;
    else delete base.creation;
  }
  return base;
}
